import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, ProgressCircle, Text } from '@valwin/facto/components';
import { CircleCheckIcon, CircleXIcon } from '@valwin/facto/icons';

import { CountdownText } from './CountdownText';

const CLOSE_DELAY_SECONDS = 3;

type SubmittingOverlayProps = {
  status: 'idle' | 'pending' | 'success' | 'error';
  onSuccess?: () => void;
};

type StatusConfig = {
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
};

export function SubmittingOverlay({
  status,
  onSuccess,
}: SubmittingOverlayProps) {
  const { t } = useTranslation();

  const [hasBeenPending, setHasBeenPending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (status === 'pending' && isDismissed) {
    setIsDismissed(false);
  }

  if (status === 'pending' && !hasBeenPending) {
    setHasBeenPending(true);
  }

  if (!hasBeenPending || isDismissed) {
    return null;
  }

  const onComplete = () => {
    if (status === 'success') {
      onSuccess?.();
    } else {
      setIsDismissed(true);
    }
  };

  const doneIconCss = { animation: 'scale-in 0.3s ease forwards' };
  const countdown = (
    <CountdownText seconds={CLOSE_DELAY_SECONDS} onComplete={onComplete} />
  );

  const statusConfig: Record<'pending' | 'success' | 'error', StatusConfig> = {
    pending: {
      icon: <ProgressCircle value={null} cap="round" />,
      title: t('integrations facebook share prepare'),
      subtitle: <Box>{t('integrations facebook share pending')}</Box>,
    },
    success: {
      icon: (
        <CircleCheckIcon boxSize="10" color="fg.success" css={doneIconCss} />
      ),
      title: t('integrations facebook share success'),
      subtitle: countdown,
    },
    error: {
      icon: <CircleXIcon boxSize="10" color="fg.error" css={doneIconCss} />,
      title: t('integrations facebook share error'),
      subtitle: countdown,
    },
  };

  const config = statusConfig[status as 'pending' | 'success' | 'error'];

  return (
    <Flex
      position="absolute"
      inset="0"
      bg="white/80"
      align="center"
      justify="center"
      borderRadius="lg"
      zIndex="1"
    >
      <Flex flexDirection="column" alignItems="center" gap="6">
        {config.icon}
        <Flex flexDirection="column" alignItems="center" gap="1">
          <Text textStyle="lg" fontWeight="bold">
            {config.title}
          </Text>
          {config.subtitle}
        </Flex>
      </Flex>
    </Flex>
  );
}
